import { SymbolActionsMenu } from '@src/components/SymbolActionsMenu';
import {
  COLUMN_GROUP_NATIVE_TYPE,
  ExpressedOutcomeEnum,
  InlineFormattedNumber,
  MixpanelEventSource,
  type Column,
  type ColumnGroup,
  type MarketAccount,
} from '@talos/kyoko';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import type { MouseEvent } from 'react';
import styled from 'styled-components';
import type { MarketSelectorValue } from '../../../MarketSelector/types';
import {
  formatPredictionPriceLabel,
  getPredictionPriceIncrement,
} from '../../../PredictionMarkets/PredictionCard/predictionDisplay';
import { predictionLegTextColor, type PredictionLeg } from '../../../PredictionMarkets/predictionLegColors';
import {
  PRICE_GRID_MARKET_DATA_LEAF_COLUMN_PROPS,
  PRICE_GRID_RFQ_COLUMN_WIDTH,
  PRICE_GRID_SYMBOL_MENU_COLUMN_WIDTH,
} from '../../columns/priceGridMarketDataColumnUtils';
import { getPriceGridRowActionColumn } from '../../columns/priceGridRowActionColumn';
import type { PriceGridActionClickHandler } from '../../hooks/actions/common/types';
import { PriceGridRFQActionButton } from '../../hooks/actions/rfq/useRFQAction';
import { getPredictionMarketDataCellValue } from './predictionMarketMarketData';
import {
  getPredictionMarketSidePrice,
  getPredictionMarketsPriceSides,
  PredictionMarketsPriceBookSide,
  type PredictionMarketsPriceSideDescriptor,
} from './priceView';
import { PredictionMarketOutcomeRow, type PredictionMarketsPriceGridRow } from './rows';
import { PredictionMarketsPriceView, type PredictionMarketsPriceView as PredictionMarketsPriceViewType } from './types';

const PREDICTION_MARKET_PRICE_GROUP_ID = 'PredictionMarketPrice';
export const PREDICTION_MARKET_PRICE_LEFT_COLUMN_ID = 'PredictionMarketPriceYes';
export const PREDICTION_MARKET_PRICE_RIGHT_COLUMN_ID = 'PredictionMarketPriceNo';
/** @deprecated Use {@link PREDICTION_MARKET_PRICE_LEFT_COLUMN_ID}. */
export const PREDICTION_MARKET_PRICE_YES_COLUMN_ID = PREDICTION_MARKET_PRICE_LEFT_COLUMN_ID;
/** @deprecated Use {@link PREDICTION_MARKET_PRICE_RIGHT_COLUMN_ID}. */
export const PREDICTION_MARKET_PRICE_NO_COLUMN_ID = PREDICTION_MARKET_PRICE_RIGHT_COLUMN_ID;
const PREDICTION_MARKET_RFQ_COLUMN_ID = 'PredictionMarketRFQ';
const PREDICTION_MARKET_MENU_COLUMN_ID = 'PredictionMarketMenu';
const PREDICTION_MARKET_QUANTITY_COLUMN_ID = 'PredictionMarketQuantity';
const PREDICTION_MARKET_PRICE_COLUMN_INITIAL_WIDTH = 80;
const PREDICTION_MARKET_QUANTITY_COLUMN_INITIAL_WIDTH = 80;

/** The columns whose cells read market data, so the grid knows when to subscribe. */
export const PREDICTION_MARKET_MARKET_DATA_COLUMN_IDS = [
  PREDICTION_MARKET_PRICE_LEFT_COLUMN_ID,
  PREDICTION_MARKET_PRICE_RIGHT_COLUMN_ID,
];

export type PredictionMarketOutcomeClickHandler = (params: {
  row: PredictionMarketOutcomeRow;
  outcome: ExpressedOutcomeEnum;
  price: string;
}) => void;

export interface PredictionMarketsPriceGridColumnParams {
  selectedMarketAccount: MarketAccount | undefined;
  priceView: PredictionMarketsPriceViewType;
  onOutcomeClick: PredictionMarketOutcomeClickHandler;
  onRFQClick: PriceGridActionClickHandler;
}

const PredictionMarketPriceCellButton = styled.button.attrs({ type: 'button' })<{ $variant: PredictionLeg }>`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  text-align: right;
  cursor: pointer;
  color: ${({ $variant, theme }) => predictionLegTextColor($variant, theme)};

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.blue.default};
    outline-offset: 1px;
  }
`;

function getPredictionMarketPriceSelectorValue(
  selectedMarketAccount: MarketAccount | undefined
): MarketSelectorValue | undefined {
  return selectedMarketAccount ? { type: 'custom', selections: [selectedMarketAccount.Name] } : undefined;
}

export function getPredictionMarketPriceVariant(
  priceView: PredictionMarketsPriceViewType,
  side: PredictionMarketsPriceSideDescriptor
): PredictionLeg {
  if (priceView === PredictionMarketsPriceView.Both) {
    return side.outcome === ExpressedOutcomeEnum.Yes ? 'yes' : 'no';
  }

  return side.bookSide === PredictionMarketsPriceBookSide.Bid ? 'yes' : 'no';
}

export function getPredictionMarketPriceCellValue({
  row,
  side,
  marketAccountName,
}: {
  row: PredictionMarketsPriceGridRow;
  side: PredictionMarketsPriceSideDescriptor;
  marketAccountName: string | undefined;
}): string | undefined {
  if (!(row instanceof PredictionMarketOutcomeRow)) {
    return undefined;
  }

  const cellValue = getPredictionMarketDataCellValue({ row, marketAccountName });
  const price = getPredictionMarketSidePrice(side, {
    yesAsk: cellValue?.offer?.Price,
    yesBid: cellValue?.bid?.Price,
  });
  return price == null ? undefined : formatPredictionPriceLabel(price, getPredictionPriceIncrement(row.security));
}

export function getPredictionMarketOutcomeClickParams({
  row,
  side,
  displayedPrice,
}: {
  row: PredictionMarketsPriceGridRow;
  side: PredictionMarketsPriceSideDescriptor;
  displayedPrice: string;
}): { outcome: ExpressedOutcomeEnum; price: string } | undefined {
  if (!(row instanceof PredictionMarketOutcomeRow)) {
    return undefined;
  }

  return {
    outcome: side.outcome,
    price: displayedPrice,
  };
}

function getPredictionMarketPriceColumn({
  colId,
  side,
  priceView,
  marketAccountName,
  onOutcomeClick,
}: {
  colId: string;
  side: PredictionMarketsPriceSideDescriptor;
  priceView: PredictionMarketsPriceViewType;
  marketAccountName: string | undefined;
  onOutcomeClick: PredictionMarketOutcomeClickHandler;
}): Column<ColDef<PredictionMarketsPriceGridRow, string>, PredictionMarketsPriceGridRow> {
  const variant = getPredictionMarketPriceVariant(priceView, side);

  return {
    type: 'custom',
    id: colId,
    title: side.header,
    initialWidth: PREDICTION_MARKET_PRICE_COLUMN_INITIAL_WIDTH,
    sortable: false,
    ...PRICE_GRID_MARKET_DATA_LEAF_COLUMN_PROPS,
    params: {
      colId,
      headerName: side.header,
      contextMenuItems: [],
      valueGetter: ({ data }: ValueGetterParams<PredictionMarketsPriceGridRow, string>) =>
        data == null ? undefined : getPredictionMarketPriceCellValue({ row: data, side, marketAccountName }),
      cellRenderer: ({ data, value }: ICellRendererParams<PredictionMarketsPriceGridRow, string>) => {
        if (!(data instanceof PredictionMarketOutcomeRow) || value == null) {
          return null;
        }

        const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          const clickParams = getPredictionMarketOutcomeClickParams({
            row: data,
            side,
            displayedPrice: value,
          });
          if (clickParams) {
            onOutcomeClick({ row: data, ...clickParams });
          }
        };

        return (
          <PredictionMarketPriceCellButton
            $variant={variant}
            onClick={handleClick}
            data-testid={`prediction-markets-price-${variant}-${side.bookSide}`}
          >
            {value}
          </PredictionMarketPriceCellButton>
        );
      },
    },
  };
}

function getPredictionMarketRFQColumn({
  marketSelectorValue,
  onRFQClick,
}: {
  marketSelectorValue: MarketSelectorValue | undefined;
  onRFQClick: PriceGridActionClickHandler;
}): Column<ColDef<PredictionMarketsPriceGridRow, PredictionMarketsPriceGridRow>, PredictionMarketsPriceGridRow> {
  return getPriceGridRowActionColumn<PredictionMarketsPriceGridRow, PredictionMarketsPriceGridRow>({
    colId: PREDICTION_MARKET_RFQ_COLUMN_ID,
    width: PRICE_GRID_RFQ_COLUMN_WIDTH,
    cellRenderer: ({ value: row }) => {
      if (!(row instanceof PredictionMarketOutcomeRow)) {
        return null;
      }

      return (
        <PriceGridRFQActionButton
          data={row}
          symbol={row.security.Symbol}
          orderQty={row.bucket.size}
          marketSelectorValue={marketSelectorValue}
          onRFQClick={onRFQClick}
        />
      );
    },
  });
}

function getPredictionMarketMenuColumn(): Column<
  ColDef<PredictionMarketsPriceGridRow, PredictionMarketsPriceGridRow>,
  PredictionMarketsPriceGridRow
> {
  return getPriceGridRowActionColumn<PredictionMarketsPriceGridRow, PredictionMarketsPriceGridRow>({
    colId: PREDICTION_MARKET_MENU_COLUMN_ID,
    width: PRICE_GRID_SYMBOL_MENU_COLUMN_WIDTH,
    cellRenderer: ({ value: row }) => {
      if (!(row instanceof PredictionMarketOutcomeRow)) {
        return null;
      }

      return (
        <SymbolActionsMenu
          security={row.security}
          mixpanelSource={MixpanelEventSource.PredictionMarketsPriceGrid}
          dataTestId={`prediction-market-symbol-menu-${row.security.Symbol}`}
          usePortal
        />
      );
    },
  });
}

export function getPredictionMarketsQuantityColumn(): Column<
  ColDef<PredictionMarketsPriceGridRow, string>,
  PredictionMarketsPriceGridRow
> {
  return {
    type: 'custom',
    id: PREDICTION_MARKET_QUANTITY_COLUMN_ID,
    title: 'Quantity',
    initialWidth: PREDICTION_MARKET_QUANTITY_COLUMN_INITIAL_WIDTH,
    sortable: false,
    suppressColumnsToolPanel: true,
    params: {
      colId: PREDICTION_MARKET_QUANTITY_COLUMN_ID,
      headerName: 'Quantity',
      valueGetter: ({ data }: ValueGetterParams<PredictionMarketsPriceGridRow, string>) =>
        data instanceof PredictionMarketOutcomeRow ? data.bucket.size : undefined,
      cellRenderer: ({ data: row, value }: ICellRendererParams<PredictionMarketsPriceGridRow, string>) =>
        row == null || value == null ? null : (
          <InlineFormattedNumber number={value} specification={row.security.MinSizeIncrement} align="right" round />
        ),
    },
  };
}

export function getPredictionMarketsPriceColumn({
  selectedMarketAccount,
  priceView,
  onOutcomeClick,
  onRFQClick,
}: PredictionMarketsPriceGridColumnParams): ColumnGroup {
  const marketAccountName = selectedMarketAccount?.Name;
  const [leftSide, rightSide] = getPredictionMarketsPriceSides(priceView);

  return {
    type: COLUMN_GROUP_NATIVE_TYPE,
    groupId: PREDICTION_MARKET_PRICE_GROUP_ID,
    headerName: 'Price',
    suppressColumnsToolPanel: true,
    marryChildren: true,
    children: [
      getPredictionMarketPriceColumn({
        colId: PREDICTION_MARKET_PRICE_LEFT_COLUMN_ID,
        side: leftSide,
        priceView,
        marketAccountName,
        onOutcomeClick,
      }),
      getPredictionMarketPriceColumn({
        colId: PREDICTION_MARKET_PRICE_RIGHT_COLUMN_ID,
        side: rightSide,
        priceView,
        marketAccountName,
        onOutcomeClick,
      }),
      getPredictionMarketRFQColumn({
        marketSelectorValue: getPredictionMarketPriceSelectorValue(selectedMarketAccount),
        onRFQClick,
      }),
      getPredictionMarketMenuColumn(),
    ],
  };
}
